/**
 * Handoff lag, the Stage 1 gate's number (PRD 3.2: "<4 hours between task
 * complete and next task start", measured as "time delta between Notify card
 * sent and next Task card started").
 *
 * Read from the audit log and nothing else, because the gate says so and
 * because the log is the record nobody can edit:
 *
 * - The clock starts when a card opens because something before it finished:
 *   the system's `not_started -> unblocked`, which is the same commit that
 *   tells its owner. Cards open from the moment the onboarding started had no
 *   handoff and are not counted.
 * - It stops at the owner's first move on that card: Start, Done (finishing
 *   without Start counts, decisions.md), or flagging it blocked -- each says
 *   somebody picked it up. Skipped cards are left out; nobody started them.
 * - Cards opened and not yet picked up are reported apart, with how long they
 *   have waited, because a median of the finished ones alone flatters a
 *   handoff that is still being dropped.
 */

import { SYSTEM } from "./engine.js";

/** The fields of an audit row this needs. */
export interface LogLine {
  at: number;
  actorId: string;
  action: string;
  taskKey: string | null;
  from: string | null;
  to: string | null;
}

export interface Handoff {
  task: string;
  openedAt: number;
  pickedUpAt: number;
  ms: number;
}

export interface HandoffReport {
  handoffs: Handoff[];
  /** Opened by a handoff and nobody has moved yet. */
  waiting: { task: string; openedAt: number }[];
  /** Null until there is one. */
  medianMs: number | null;
}

const PICKED_UP = new Set(["in_progress", "blocked", "completed"]);

export function handoffLags(lines: readonly LogLine[]): HandoffReport {
  const ordered = [...lines].sort((a, b) => a.at - b.at);
  const started = ordered.find((line) => line.action === "start")?.at ?? ordered[0]?.at ?? 0;

  const opened = new Map<string, number>();
  const handoffs: Handoff[] = [];
  const done = new Set<string>();

  for (const line of ordered) {
    if (!line.taskKey || line.action !== "state") continue;
    const task = line.taskKey;
    if (line.actorId === SYSTEM && line.from === "not_started" && line.to === "unblocked") {
      // Open from the start: not a handoff.
      if (line.at > started) opened.set(task, line.at);
      continue;
    }
    if (line.to === "skipped") {
      opened.delete(task);
      continue;
    }
    const at = opened.get(task);
    if (at === undefined || done.has(task) || line.actorId === SYSTEM || !PICKED_UP.has(line.to ?? ""))
      continue;
    handoffs.push({ task, openedAt: at, pickedUpAt: line.at, ms: line.at - at });
    done.add(task);
  }

  const waiting = [...opened.entries()]
    .filter(([task]) => !done.has(task))
    .map(([task, openedAt]) => ({ task, openedAt }));
  return { handoffs, waiting, medianMs: median(handoffs.map((handoff) => handoff.ms)) };
}

export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
}

/** The gate's line (PRD 3.2). */
export const HANDOFF_TARGET_MS = 4 * 3_600_000;
